import { apiService } from './api';
import { UserRole } from '../types';

export interface User {
  user_id: string;
  email: string;
  full_name?: string;
  /** Shown on tasks and avatars; blank = derived from the email. */
  display_name?: string | null;
  is_approved: boolean;
  role: UserRole;
  create_time: string;
  update_time: string;
  /** False once an Owner deactivates the account (can't sign in; history kept). */
  is_active?: boolean;
  deactivated_at?: string | null;
  deactivated_by_user_id?: string | null;
  deactivation_note?: string | null;
}

/** What a staff member has done — anyone with history can't be deleted. */
export interface UserHistory {
  counts: { label: string; count: number }[];
  total: number;
  can_delete: boolean;
  open_tasks: number;
}

export type OpenTasksAction = 'keep' | 'unassign' | 'reassign';

export interface AdminStats {
  total_users: number;
  approved_users: number;
  pending_users: number;
  owners: number;
}

export type PasswordResetRequestStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'COMPLETED'
  | 'EXPIRED';

export interface PasswordResetRequestItem {
  request_id: string;
  user_id: string;
  user_email?: string;
  status: PasswordResetRequestStatus;
  initiated_by: 'SELF_SERVICE' | 'ADMIN';
  requested_at: string;
  requested_ip?: string;
  reviewed_by_user_id?: string;
  reviewed_at?: string;
  review_note?: string;
  reset_token_expires?: string;
  completed_at?: string;
}

export interface PasswordResetRequestList {
  items: PasswordResetRequestItem[];
  pending_count: number;
}

export interface PasswordResetApproval {
  request_id: string;
  reset_token: string;
  expires_at: string;
}

class AdminService {
  private baseUrl = '/admin';

  // Get all users (super admin only)
  async getAllUsers(): Promise<User[]> {
    return apiService.get<User[]>(`${this.baseUrl}/users`);
  }

  // Get pending approval users
  async getPendingUsers(): Promise<User[]> {
    return apiService.get<User[]>(`${this.baseUrl}/users/pending`);
  }

  // Approve a user
  async approveUser(userId: string): Promise<User> {
    return apiService.post<User>(`${this.baseUrl}/users/${userId}/approve`);
  }

  // Reject/delete a user
  async rejectUser(userId: string): Promise<void> {
    return apiService.post<void>(`${this.baseUrl}/users/${userId}/reject`);
  }

  // Revoke user approval
  async revokeUserApproval(userId: string): Promise<User> {
    return apiService.post<User>(`${this.baseUrl}/users/${userId}/revoke`);
  }

  // What they've done (decides whether they can be deleted) + open tasks
  async getUserHistory(userId: string): Promise<UserHistory> {
    return apiService.get<UserHistory>(`${this.baseUrl}/users/${userId}/history`);
  }

  // Deactivate: no sign-in, sessions end, history kept (Owner only)
  async deactivateUser(
    userId: string,
    data: { note?: string | null; open_tasks: OpenTasksAction; reassign_to_user_id?: string | null },
  ): Promise<{ user: User; tasks_moved: number }> {
    return apiService.post(`${this.baseUrl}/users/${userId}/deactivate`, data);
  }

  async reactivateUser(userId: string): Promise<User> {
    return apiService.post<User>(`${this.baseUrl}/users/${userId}/reactivate`);
  }

  // Permanently delete — only accounts with no history (Owner only)
  async deleteUser(userId: string): Promise<void> {
    return apiService.delete<void>(`${this.baseUrl}/users/${userId}`);
  }

  // Change a user's role (Owner only)
  async setUserRole(userId: string, role: UserRole): Promise<User> {
    return apiService.post<User>(`${this.baseUrl}/users/${userId}/role`, { role });
  }

  // Get admin statistics
  async getAdminStats(): Promise<AdminStats> {
    return apiService.get<AdminStats>(`${this.baseUrl}/stats`);
  }

  // --- Password resets (no-OTP recovery — see design doc) ---

  // List password reset requests, optionally filtered by status
  async getPasswordResetRequests(
    status?: PasswordResetRequestStatus
  ): Promise<PasswordResetRequestList> {
    const query = status ? `?status=${status}` : '';
    return apiService.get<PasswordResetRequestList>(
      `${this.baseUrl}/password-reset-requests${query}`
    );
  }

  // Approve a pending request — returns the one-time reset link, shown only once
  async approvePasswordResetRequest(
    requestId: string,
    reviewNote?: string
  ): Promise<PasswordResetApproval> {
    return apiService.post<PasswordResetApproval>(
      `${this.baseUrl}/password-reset-requests/${requestId}/approve`,
      { review_note: reviewNote }
    );
  }

  // Reject a pending request
  async rejectPasswordResetRequest(
    requestId: string,
    reviewNote?: string
  ): Promise<PasswordResetRequestItem> {
    return apiService.post<PasswordResetRequestItem>(
      `${this.baseUrl}/password-reset-requests/${requestId}/reject`,
      { review_note: reviewNote }
    );
  }

  // Reset a user's password directly, without them requesting it first.
  // A reason note is required.
  async directResetUserPassword(
    userId: string,
    reviewNote: string
  ): Promise<PasswordResetApproval> {
    return apiService.post<PasswordResetApproval>(
      `${this.baseUrl}/users/${userId}/reset-password`,
      { review_note: reviewNote }
    );
  }
}

export const adminService = new AdminService();
